import { Injectable } from '@angular/core';

import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { forkJoin, NEVER, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { GlobalSettingsService } from '../services/ui/global-settings.service';
import { MyWorkService } from '../services/sections/job/my-work.service';
import { JobService } from '../services/sections/job/job.service';
import { Job } from '../models/job.model';
import { PageType } from '../models/enum/page-type.enum';
import { ServiceContractJobType } from '../models/enum/service-contract-job-type.enum';
import { NavigationActionButton } from '../components/navigation-bar/models/navigation-action-button.model';
import { DigitalLogService } from '../services/sections/job/digital-log.service';
import { JobStatusTypes } from '../models/enum/job-status-types.enum';

@Injectable({
	providedIn: 'root'
})
export class MyWorkUrlResolver<T> implements Resolve<Observable<string>>{

	public static isCustomerAllowed = false;
	constructor(
		private router: Router,
		private myWorkService: MyWorkService,
		private jobService: JobService,
		private digitalLogService: DigitalLogService
	) { }

	resolve(activatedRouteSnapshot: ActivatedRouteSnapshot, routerStateSnapshot: RouterStateSnapshot) {

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		return forkJoin([
			this.myWorkService.getRedirectUri(activatedRouteSnapshot.paramMap.get('token')),
			this.myWorkService.isVendor(activatedRouteSnapshot.paramMap.get('token')),
			this.jobService.getJobDetails(activatedRouteSnapshot.paramMap.get('token')),
			this.digitalLogService.allowOnDemandFormRequest(activatedRouteSnapshot.paramMap.get('token'))
		])
			.pipe(
				catchError((err) => {
					this.router.navigate(['/404']);
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					return NEVER;
				}),
				map(([uri, isVendor, job, hasCustomerPermission]) => {
					let stringUrl = null;
					if (!!uri) {
						stringUrl = uri.smartSyncUri;
					}

					return [stringUrl, isVendor, job, hasCustomerPermission];
				}),
				tap(([uri, isVendor, job, hasCustomerPermission]) => {
					MyWorkUrlResolver.isCustomerAllowed = hasCustomerPermission;
					const pageType = activatedRouteSnapshot.data.pageType;
					const isCustomerCompliant = pageType === PageType.Customer &&
						[ServiceContractJobType.Regular, ServiceContractJobType.DigitalLogbook, ServiceContractJobType.SmartDocs].includes(job.jobTypeId);

					if (uri) {
						GlobalSettingsService.navigationBarActions = new Map<string, NavigationActionButton>();
						const returnUrl = sessionStorage.getItem('binder-rurl');
						if (!returnUrl && (isVendor || isCustomerCompliant)) {
							GlobalSettingsService.navigationBarActions.set(
								'my-work-access-button',
								{
									icon: 'list_alt',
									listOrder: 100,
									text: 'My Work',
									isInDropdownMenu: false,
									isTextOnlyShownInDropdown: true,
									isDisabled: job.statusId === JobStatusTypes.Canceled,
									action: () => this.router.navigate([uri])
								}
							);
						}
						if (returnUrl) {
							GlobalSettingsService.navigationBarActions.set(
								'digital-logbooks-return-button',
								{
									icon: 'assignment',
									listOrder: 100,
									text: 'Back to Binder',
									isInDropdownMenu: false,
									isTextOnlyShownInDropdown: true,
									action: () => this.router.navigate([returnUrl])
								}
							);
						}
					}
				}),
				map(([uri, isVendor, job, hasCustomerPermission]) => uri)
			);
	}
}
